<template>
  <div ref="$comment" class="commentArea" @click="onClickComment" />
  <!-- <PopupConfirm :is-opened="isConfirmOpened" :type="confirmType" :message="confirmMsg" @confirm="getConfirmed" /> -->
</template>

<script lang="ts" setup>
const props = defineProps<{
  commentToken: string
  ws2Token?: string
  bestComments?: number[]
  adminComments?: ReviewDetail['adminComments']
}>()

const emits = defineEmits(['count'])

useHead({
  script: [{ src: `https://pic.watv.org/watvcomment/watvComment@next.js?v=251205` }],
})

const { locale } = useI18n()
const { showLoginPopup } = useAuth()
const { translateAdminComment } = useReview()
const { showToast } = useToast()
const { $t } = useNuxtApp()

const $comment = ref<HTMLElement>()
const comment = ref<any | null>(null)

const isConfirmOpened = ref(false)
const confirmType = ref('')
const confirmMsg = ref('')
let getConfirmed: any

onMounted(async () => {
  while (!(window as any).WatvComment) {
    await new Promise(r => setTimeout(r, 100))
  }

  comment.value = new (window as any).WatvComment(
    {
      commentToken: props.commentToken,
      ws2Token: props.ws2Token,
      editorConfig: {
        template: {
          paddingWidth: '13px',
          paddingHeight: '13px',
        },
      },
      ui: {
        showLoginRequired: {
          active: true,
          onclick: () => {
            showLoginPopup()
          },
        },
        layout: {
          commentHeader: (comment: any) => `
            <div class="wc-chead">
              <div class="wc-cinfo">
                <div class="wc-infodetail">
                  <div class="wc-cname">
                    {{ name }}
                    {{ new }}
                    {{ secret }}
                  </div>
                </div>
              </div>
            </div>
          `,
          commentFooter: (comment: any) => {
            const isBestComment = props.bestComments && props.bestComments.includes(comment.id)
            const bestCommentHtml = !isBestComment
              ? ''
              : `
              <span class="best-comment">
                <img class="img-profile" src="/images/flower2.svg">
                <img class="img-heart" src="/images/ico_heart.svg">
                <div class="img-tip">${$t('운영진의heart')}</div>
              </span>
            `
            return `
              <div class="wc-cdate">{{ date }}{{ read }}</div>
              <div class="wc-cfoot">{{reply}}{{edit}}{{delete}}{{report}}{{translate}}</div>
              <div class="wc-cfoot2">{{like}}${bestCommentHtml}</div>
            `
          },
        },
        pinnedCommentIds: props.bestComments,
      },
      // confirm: async (type: string, message: string) => {
      //   if (type === 'comment.delete')
      //     confirmType.value = 'delete'

      //   confirmMsg.value = message
      //   isConfirmOpened.value = true

      //   return new Promise((resv) => {
      //     getConfirmed = (isConfirmed: boolean) => {
      //       isConfirmOpened.value = false
      //       resv(isConfirmed)
      //     }
      //   })
      // },
      alert: async (type: string, message: string) => {
        if (type == 'like.change') {
          showLoginPopup()
          return true
        }
      },
    },
    $comment.value,
  )
  comment.value.lang = locale.value
  comment.value.on('count', (e: any) => {
    emits('count', e.data)
  })
})
onBeforeUnmount(() => {
  comment.value?.destroy()
  comment.value = null
})

watch([comment, () => props.bestComments], () => {
  if (comment.value) {
    comment.value.config.ui.pinnedCommentIds = props.bestComments
  }
})

watch(
  [comment, () => props.adminComments],
  () => {
    if (comment.value) {
      if ((props.adminComments?.length ?? 0) > 0) {
        comment.value.config.ui.layout.commentList = () => `
        {{header}}
        {{editor}}
        {{list-options}}
        <div class="manager-comment-wrap">
          ${props
            .adminComments!
            .map(
              c => `
              <div class="manager-comment" comment-id="${c.id}">
                <span class="thumb"><img src="/images/profile_admin.jpg" /></span>
                <div class="content-box">
                  <div class="info">${$t('운영진')}</div>
                  <div class="comment">${safetyText(c.comment)}</div>
                  <button class="btn-translate" ${
                    isKorean(c.comment) && locale.value == 'ko' ? 'style="display:none"' : ''
                  }>${$t('번역하기')}</button>
                  <div class="bottom">
                    <img class="applause" src="/images/applause.svg" alt="" />
                  </div>
                </div>
              </div>
            `,
            )
            .join('')}
        </div>
        {{list}}
        {{paging}}
      `
      }
      else {
        comment.value.config.ui.layout.commentList = () => `
        {{header}}
        {{editor}}
        {{list-options}}
        {{list}}
        {{paging}}
      `
      }
    }
  },
  { immediate: true },
)

async function onClickComment(e: Event) {
  const $target = e.target as HTMLElement

  if ($target.closest?.('.btn-translate')) {
    const $box: HTMLElement = $target.closest('.manager-comment')!
    const commentId = +$box.getAttribute('comment-id')!
    const comment = props.adminComments!.find(c => c.id == commentId)!

    if (comment.viewMode == 'loading')
      return

    try {
      if (comment.translated) {
        comment.viewMode = comment.viewMode == 'translated' ? undefined : 'translated'
      }
      else {
        comment.viewMode = 'loading'
        updateDisplay(comment)
        const data = await translateAdminComment(comment.id)
        comment.translated = data!
        comment.viewMode = 'translated'
      }
    }
    catch (e) {
      showToast({ message: '오류가 발생했습니다.' })
      comment.viewMode = undefined
    }
    updateDisplay(comment)
  }
}

function updateDisplay(comment: ReviewDetail['adminComments'][number]) {
  const $box = $comment.value?.querySelector(`.manager-comment[comment-id="${comment.id}"]`)
  if (!$box)
    return

  $box.classList.toggle('translating', comment.viewMode == 'loading')
  $box.classList.toggle('translated', comment.viewMode == 'translated')

  const $content = $box.querySelector('.comment')!
  if (comment.viewMode == 'translated') {
    $content.innerHTML = safetyText(comment.translated!)
  }
  else {
    $content.innerHTML = safetyText(comment.comment)
  }

  const $btnTranslate = $box.querySelector<HTMLElement>('.btn-translate')!
  $btnTranslate.style.display = isKorean(comment.comment) && locale.value == 'ko' ? 'none' : ''
  if (comment.viewMode == 'loading') {
    $btnTranslate.textContent = `${$t('번역중')}...`
  }
  else if (comment.viewMode == 'translated') {
    $btnTranslate.textContent = $t('원문보기')
  }
  else {
    $btnTranslate.textContent = $t('번역하기')
  }
}

function isKorean(text: string) {
  if (text.length == 0)
    return false
  return text.replace(/[^가-힣0-9ㄱ-ㅎㅏ-ㅣ]/g, '').length >= text.length / 2
}
</script>
